"use client";
import React, { useState, useMemo } from "react";
import { IconClock, IconCalendarEvent, IconTag } from "@tabler/icons-react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import img from "../assets/noNotifications.png";
import "./notifications.css";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";
import { useAppDispatch, useAppSelector } from "../hooks/redux/useRedux";
import {
  selectNotification,
  selectNotificationLoading,
} from "../redux/slices/notificationSlice/NotificationSlice";
import { postNotificationViewedThunk } from "../redux/slices/notificationSlice/asyncthunk";

export default function NotificationPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const notificationList = useAppSelector(selectNotification);
  const loading = useAppSelector(selectNotificationLoading);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Unviewed first; list comes from the store (fetched once in Home).
  const notifications = useMemo(() => {
    const list = Array.isArray(notificationList?.earlier)
      ? notificationList.earlier
      : [];
    return [...list].sort((a, b) =>
      a.viewed === b.viewed ? 0 : a.viewed ? 1 : -1,
    );
  }, [notificationList]);

  const markNotificationViewed = (item) => {
    if (!item?.id) return;
    if (!item.viewed) dispatch(postNotificationViewedThunk(item.id));
    setSelectedNotification(item);
  };

  return (
    <div className="notification-panel">
      <div className="notif-header">
        <div className="notif-header-left">
          <h2>{t("home.notificationsTitle")}</h2>
          {notifications.length > 0 && (
            <span className="notif-count">{notifications.length}</span>
          )}
        </div>
      </div>

      {loading && (
        <div className="notif-empty">{t("home.loadingNotifications")}</div>
      )}

      {!loading && notifications.length === 0 && (
        <div className="notif-empty">
          <img src={img} alt={t("home.noNotificationsImageAlt")} />
          <p>{t("home.noNotificationsYet")}</p>
        </div>
      )}

      {!loading && notifications.length > 0 && (
        <div className="notif-list">
          {notifications.map((item) => {
            const info = item.appointment_info || {};
            const currencyCode = info.currency || "USD";

            const price = info.price
              ? `${getCurrencySymbol(currencyCode)}${info.price}`
              : "";

            return (
              <div
                key={item.id}
                className={`notif-item ${item.viewed ? "viewed" : ""}`}
                onClick={() => markNotificationViewed(item)}
              >
                <img
                  src={info.image || "https://i.pravatar.cc/100"}
                  alt={t("home.clientAvatarAlt")}
                  className="notif-avatar"
                />

                <div className="notif-content">
                  <div className="notif-title-row">
                    <h3 className="notif-name">
                      {info.client_name || t("home.defaultClientName")}
                    </h3>
                    {!item.viewed && <span className="notif-dot" />}
                  </div>

                  <div className="notif-meta">
                    <div className="notif-meta-item">
                      <IconCalendarEvent className="notif-icon" />
                      <span>
                        {item.appointment_date || t("home.defaultDateFallback")}
                      </span>
                    </div>
                    <div className="notif-meta-item">
                      <IconClock className="notif-icon" />
                      <span>
                        {t("home.timeRange", {
                          startTime:
                            item.appointment_time ||
                            t("home.defaultTimeFallback"),
                          endTime:
                            item.appointment_end_time ||
                            t("home.defaultTimeFallback"),
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="notif-service">
                    <IconTag className="notif-icon" />
                    <span>
                      {price
                        ? t("home.serviceWithPrice", {
                            serviceName:
                              info.service_name || t("home.defaultServiceName"),
                            price,
                          })
                        : info.service_name || t("home.defaultServiceName")}
                    </span>
                  </div>

                  <p className="notif-message">
                    {info.service_description ||
                      t("home.defaultDescriptionFallback")}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {selectedNotification && (
          <motion.div
            className="notif-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedNotification(null)}
          >
            <motion.div
              className="notif-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="notif-modal-header">
                <h3>{t("home.appointmentDetailsTitle")}</h3>
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="notif-modal-close"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="notif-modal-content">
                <img
                  src={
                    selectedNotification.appointment_info?.image ||
                    "https://i.pravatar.cc/150"
                  }
                  alt={t("home.clientAvatarAlt")}
                  className="notif-modal-avatar"
                />

                <h4>
                  {selectedNotification.appointment_info?.client_name ||
                    t("home.defaultClientName")}
                </h4>

                <p className="notif-modal-service">
                  {selectedNotification.appointment_info?.service_name ||
                    t("home.defaultServiceName")}
                </p>

                <div className="notif-modal-info">
                  <p>
                    <strong>{t("home.labelDate")}</strong>{" "}
                    {selectedNotification.appointment_date ||
                      t("home.defaultDateFallback")}
                  </p>
                  <p>
                    <strong>{t("home.labelTime")}</strong>{" "}
                    {t("home.timeRange", {
                      startTime:
                        selectedNotification.appointment_time ||
                        t("home.defaultTimeFallback"),
                      endTime:
                        selectedNotification.appointment_end_time ||
                        t("home.defaultTimeFallback"),
                    })}
                  </p>
                  <p>
                    <strong>{t("home.labelPrice")}</strong>{" "}
                    {selectedNotification.appointment_info?.price
                      ? `${getCurrencySymbol(
                          selectedNotification.appointment_info?.currency ||
                            "USD",
                        )}${selectedNotification.appointment_info.price}`
                      : t("home.defaultDateFallback")}
                  </p>
                  <p>
                    <strong>{t("home.labelDescription")}</strong>{" "}
                    {selectedNotification.appointment_info
                      ?.service_description ||
                      t("home.defaultDescriptionFallback")}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
